import { useRef, useCallback, useEffect, useState } from 'react';
import { debugError, debugLog, debugWarn } from '../../utils/debugLog';

/**
 * Configuration options for delayed callback behavior
 */
export interface DelayedCallbackConfig {
  /** Default delay in milliseconds (default: 1000) */
  defaultDelay?: number;
  /** Whether to automatically clear timer on unmount (default: true) */
  autoCleanup?: boolean;
  /** Whether to enable debug logging (default: false) */
  enableLogging?: boolean;
  /** Maximum number of pending callbacks allowed (default: 1) */
  maxPending?: number;
}

/**
 * Information about the delayed callback state
 */
export interface DelayedCallbackState {
  /** Whether a callback is currently pending */
  isPending: boolean;
  /** Number of pending callbacks */
  pendingCount: number;
  /** Timestamp when the current callback was scheduled */
  scheduledAt: number | null;
  /** Delay for the current pending callback */
  currentDelay: number | null;
}

/**
 * Return type for the useDelayedCallback hook
 */
export interface UseDelayedCallbackReturn {
  /** Current state of delayed callbacks */
  state: DelayedCallbackState;
  /** Schedule a callback to execute after delay */
  scheduleCallback: (callback: () => void | Promise<void>, delay?: number) => void;
  /** Cancel all pending callbacks */
  cancelCallback: () => void;
  /** Check if any callbacks are pending */
  isPending: boolean;
  /** Get the number of pending callbacks */
  getPendingCount: () => number;
  /** Clear all timers and reset state */
  clearAll: () => void;
}

/**
 * Timer management utility for tracking active timers
 */
class TimerManager {
  private timers: Set<NodeJS.Timeout> = new Set();
  private enableLogging: boolean;

  constructor(enableLogging: boolean = false) {
    this.enableLogging = enableLogging;
  }

  /**
   * Add a timer to the managed set
   */
  addTimer(timer: NodeJS.Timeout): void {
    this.timers.add(timer);
    
    if (this.enableLogging) {
      debugLog(`⏰ Timer added. Active timers: ${this.timers.size}`);
    }
  }

  /**
   * Remove a timer from the managed set
   */
  removeTimer(timer: NodeJS.Timeout): void {
    this.timers.delete(timer);
    
    if (this.enableLogging) {
      debugLog(`⏰ Timer removed. Active timers: ${this.timers.size}`);
    }
  }

  /**
   * Clear a specific timer
   */
  clearTimer(timer: NodeJS.Timeout): void {
    clearTimeout(timer);
    this.removeTimer(timer);
  }

  /**
   * Clear all managed timers
   */
  clearAll(): void {
    const count = this.timers.size;
    
    this.timers.forEach(timer => {
      clearTimeout(timer);
    });
    
    this.timers.clear();
    
    if (this.enableLogging && count > 0) {
      debugLog(`🗑️ Cleared ${count} timers`);
    }
  }

  /**
   * Get the number of active timers
   */
  getActiveCount(): number {
    return this.timers.size;
  }

  /**
   * Check if any timers are active
   */
  hasActiveTimers(): boolean {
    return this.timers.size > 0;
  }
}

/**
 * Hook for managing delayed callback execution with automatic cleanup
 * 
 * Features:
 * - Automatic timer cleanup on unmount
 * - Multiple pending callback management
 * - Memory leak prevention
 * - Configurable delay and behavior
 * - Debug logging for timer tracking
 * - State tracking for pending callbacks
 * 
 * @param config - Configuration options for callback behavior
 * @returns Object with callback scheduling and management functions
 */
export const useDelayedCallback = (config: DelayedCallbackConfig = {}): UseDelayedCallbackReturn => {
  const {
    defaultDelay = 1000,
    autoCleanup = true,
    enableLogging = false,
    maxPending = 10, // Allow multiple callbacks by default
  } = config;

  // Refs for persistent state and timer management
  const timerManager = useRef(new TimerManager(enableLogging));
  const pendingCallbacks = useRef<Map<NodeJS.Timeout, {
    callback: () => void | Promise<void>;
    scheduledAt: number;
    delay: number;
  }>>(new Map());

  // State for reactive updates
  const [, setStateVersion] = useState(0);

  // Force re-render when state changes
  const forceUpdate = useCallback(() => {
    setStateVersion(prev => prev + 1);
  }, []);

  // Get current state
  const getCurrentState = useCallback((): DelayedCallbackState => {
    const pendingCount = pendingCallbacks.current.size;
    const isPending = pendingCount > 0;
    
    // Get the most recent callback info
    let scheduledAt: number | null = null;
    let currentDelay: number | null = null;
    
    if (isPending) {
      const entries = Array.from(pendingCallbacks.current.values());
      const mostRecent = entries.reduce((latest, current) => 
        current.scheduledAt > latest.scheduledAt ? current : latest
      );
      scheduledAt = mostRecent.scheduledAt;
      currentDelay = mostRecent.delay;
    }

    return {
      isPending,
      pendingCount,
      scheduledAt,
      currentDelay,
    };
  }, []);

  /**
   * Execute a callback safely with error handling
   */
  const executeCallback = useCallback(async (callback: () => void | Promise<void>) => {
    try {
      const result = callback();
      
      // Handle both sync and async callbacks
      if (result instanceof Promise) {
        await result;
      }
      
      if (enableLogging) {
        debugLog('✅ Delayed callback executed successfully');
      }
    } catch (error) {
      debugError('❌ Error executing delayed callback:', error);
    }
  }, [enableLogging]);

  /**
   * Schedule a callback to execute after the specified delay
   */
  const scheduleCallback = useCallback((
    callback: () => void | Promise<void>,
    delay: number = defaultDelay
  ) => {
    if (typeof callback !== 'function') {
      debugWarn('useDelayedCallback: Invalid callback provided');
      return;
    }

    if (delay < 0) {
      debugWarn('useDelayedCallback: Negative delay provided, using 0');
      delay = 0;
    }

    // Check if we've reached the maximum pending callbacks
    if (pendingCallbacks.current.size >= maxPending) {
      if (enableLogging) {
        debugWarn(`⚠️ Maximum pending callbacks (${maxPending}) reached. Cancelling oldest.`);
      }
      
      // Cancel the oldest callback
      const oldestTimer = Array.from(pendingCallbacks.current.keys())[0];
      if (oldestTimer) {
        timerManager.current.clearTimer(oldestTimer);
        pendingCallbacks.current.delete(oldestTimer);
      }
    }

    const scheduledAt = Date.now();
    
    // Create the timer
    const timer = setTimeout(async () => {
      // Remove from pending callbacks first
      pendingCallbacks.current.delete(timer);
      timerManager.current.removeTimer(timer);
      
      // Execute the callback
      await executeCallback(callback);
      
      // Force update to reflect state change after callback execution
      forceUpdate();
    }, delay);

    // Track the timer and callback info
    timerManager.current.addTimer(timer);
    pendingCallbacks.current.set(timer, {
      callback,
      scheduledAt,
      delay,
    });

    if (enableLogging) {
      debugLog(`⏱️ Callback scheduled with ${delay}ms delay. Pending: ${pendingCallbacks.current.size}`);
    }

    // Force update to reflect state change
    forceUpdate();
  }, [defaultDelay, maxPending, enableLogging, executeCallback, forceUpdate]);

  /**
   * Cancel all pending callbacks
   */
  const cancelCallback = useCallback(() => {
    const count = pendingCallbacks.current.size;
    
    // Clear all timers
    timerManager.current.clearAll();
    
    // Clear pending callbacks map
    pendingCallbacks.current.clear();
    
    if (enableLogging && count > 0) {
      debugLog(`🚫 Cancelled ${count} pending callbacks`);
    }

    // Force update to reflect state change
    forceUpdate();
  }, [enableLogging, forceUpdate]);

  /**
   * Check if any callbacks are pending
   */
  const isPending = useCallback((): boolean => {
    return pendingCallbacks.current.size > 0;
  }, []);

  /**
   * Get the number of pending callbacks
   */
  const getPendingCount = useCallback((): number => {
    return pendingCallbacks.current.size;
  }, []);

  /**
   * Clear all timers and reset state
   */
  const clearAll = useCallback(() => {
    timerManager.current.clearAll();
    pendingCallbacks.current.clear();
    
    if (enableLogging) {
      debugLog('🧹 All timers and callbacks cleared');
    }

    // Force update to reflect state change
    forceUpdate();
  }, [enableLogging, forceUpdate]);

  // Automatic cleanup on unmount
  useEffect(() => {
    if (!autoCleanup) {
      return;
    }

    const manager = timerManager.current;
    const callbacks = pendingCallbacks.current;

    return () => {
      manager.clearAll();
      callbacks.clear();
    };
  }, [autoCleanup]);

  // Return the hook interface
  return {
    state: getCurrentState(),
    scheduleCallback,
    cancelCallback,
    isPending: isPending(),
    getPendingCount,
    clearAll,
  };
};

export default useDelayedCallback;
